import { FormattedMessage } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import { useReportSearchMapRowPrice } from 'components/Search/SearchResultsMap/SearchMapRowPrices'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { ViewOfferButton } from './ViewOfferButton'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowLuxPlusPriceLoadingSkeleton from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceLoadingSkeleton'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import CruisePriceFromLabel from 'components/Cruises/Common/CruisePriceFromLabel'
import CruisePriceSaveUpCaption from 'components/Cruises/Common/CruisePriceSaveUpCaption'
import CruisePromotionPriceUnit from 'components/Cruises/Common/CruisePromotionPriceUnit'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import type { CruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'

interface Props {
  isFlash?: boolean
  roomOccupancy?: string
  priceDetails: App.Pricing
  memberPrice?: number
  memberPriceType?: App.Cruises.CruiseMemberPriceType
  isPriceLoading?: boolean
  className?: string
  offerId: string
  variant?: 'default' | 'condensed'
  filters?: App.OfferListFilters
  loyaltyProductType?: App.LoyaltyCruiseProductType
  showViewButton?: boolean
  promotionalPrice?: CruiseOfferPriceVariation['promotionalPrice']
  cabinCategory?: string
  discountAmount?: number
}

function CruisePriceDetails(props: Props) {
  const {
    variant = 'default',
    priceDetails,
    memberPrice,
    memberPriceType,
    isPriceLoading,
    isFlash,
    roomOccupancy,
    offerId,
    filters,
    loyaltyProductType,
    showViewButton = true,
    promotionalPrice,
    cabinCategory,
    discountAmount,
  } = props
  const perPersonPrice = promotionalPrice?.perPersonPrice
  const hasPerPersonPrice = !!perPersonPrice
  useReportSearchMapRowPrice(perPersonPrice)
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinType = cabinCategory ? cabinTypeLabels[cabinCategory.toLowerCase()] ?? cabinCategory : undefined
  const { priceInPoints, memberPriceInPoints, pointsToBurn, pointsOnly, hideZeroCash, shouldShowBurn } = useSearchOfferBurnPricing({
    price: priceDetails.price,
    memberPrice,
    loyaltyProductType,
    viewPriceInPoints: filters?.viewPriceInPoints,
  })

  return (
    <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="end">
      <Group direction="vertical" gap={8}>
        <div>
          {isPriceLoading && <>
            <PriceRowPriceCaptionLoadingSkeleton width="4ch" />
            <PriceRowLuxPlusPriceLoadingSkeleton size="L" />
          </>}
          {!isPriceLoading && promotionalPrice && <>
            <PriceRowPriceCaption>
              <CruisePriceFromLabel promotion={promotionalPrice.promotion} cabinType={cabinType} />
            </PriceRowPriceCaption>
            {hasPerPersonPrice ? <>
              <Caption variant="medium" colour="success-dark">
                <FormattedMessage
                  id="01ZsUhS60i_gK_k4VanJ"
                  defaultMessage="{price}/person"
                  
                  values={{
                    price: <Heading as="span" variant="heading4" colour="success-dark">
                      <FormatCurrency value={perPersonPrice} />
                    </Heading>,
                  }}
                />
              </Caption>
              <Caption variant="medium" colour="light-foreground-alt">
                <FormatCurrency value={promotionalPrice.total} />&nbsp;
                <CruisePromotionPriceUnit promotion={promotionalPrice.promotion} />
              </Caption>
            </> : <LuxPlusPriceStack size="L" price={promotionalPrice.total} saleUnit={promotionalPrice.type} />}
          </>}
          {!isPriceLoading && !promotionalPrice && <>
            <PriceRowPriceCaption><CruisePriceFromLabel /></PriceRowPriceCaption>
            {shouldShowBurn && memberPriceType !== 'STANDARD_PRICE' && <LoyaltyBurnPriceDisplay
              totalPrice={priceDetails.price}
              pointsToBurn={pointsToBurn}
              priceInPoints={priceInPoints}
              memberPrice={memberPrice}
              memberPriceInPoints={memberPriceInPoints}
              size="L"
              saleUnit={priceDetails.saleUnit}
              pointsOnly={pointsOnly}
              hideZeroCash={hideZeroCash}
            />}
            {(!shouldShowBurn || memberPriceType === 'STANDARD_PRICE') && <Group direction="horizontal" gap={4} verticalAlign="end">
              <LuxPlusPriceStack
                size="L"
                price={priceDetails.price}
                memberPrice={memberPrice}
                saleUnit={priceDetails.saleUnit}
                mode={memberPriceType === 'STANDARD_PRICE' ? 'member-only' : undefined}
              />
            </Group>}
          </>}
          {!isPriceLoading && promotionalPrice && !!discountAmount && discountAmount > 0 && <CruisePriceSaveUpCaption
            size="M"
            discountAmount={discountAmount}
            discountPercentage={priceDetails.discountPercent}
          />}
          {!isPriceLoading && !promotionalPrice && !!priceDetails.value && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={priceDetails.value}
            discountPercentage={priceDetails.discountPercent}
          />}
          {isFlash && !!roomOccupancy && <PriceRowCaption>{roomOccupancy}</PriceRowCaption>}
        </div>
        <PriceRowAgentHubCommission
          productType={isFlash ? 'cruise-flash' : 'cruise'}
          offerId={offerId}
        />
      </Group>
      {showViewButton && <ViewOfferButton variant={variant} />}
    </Group>
  )
}

export default CruisePriceDetails
